import { ButtonInputWithPopover, SelectMultiple } from "@msys/ui";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { ProjectStateMachineStatus } from "../../../../clients/graphqlTypes.js";
import {
  FilterChipGroup,
  FilterChipGroupProps,
} from "../../../commons/filters/FilterChip.js";
import { FilterProps } from "../../../commons/filters/index.js";
import { useProjectStates } from "../useProjectStates.js";

export function ProjectStatesFilterForm({
  value,
  setValue,
  allowedStates,
}: FilterProps<ProjectStateMachineStatus[]> & {
  allowedStates: ProjectStateMachineStatus[];
}) {
  const { t } = useTranslate(["Global"]);
  const { projectStateOptions } = useProjectStates();
  const options = projectStateOptions.filter(o =>
    allowedStates.includes(o.value)
  );

  if (options.length <= 1) return null;

  return (
    <Box p={1}>
      <FormControl component="fieldset" variant="standard">
        <FormGroup>
          {options.map((option, index) => (
            <FormControlLabel
              control={
                <Checkbox
                  size="medium"
                  checked={value.includes(option.value)}
                  onChange={(event, checked) => {
                    if (checked) {
                      setValue(Array.from(new Set([...value, option.value])));
                    } else {
                      setValue(value.filter(v => v !== option.value));
                    }
                  }}
                />
              }
              label={<Typography variant="body1">{option.label}</Typography>}
              sx={{ ml: 0, mr: 0 }}
              key={`${option.value}-${index}`}
            />
          ))}
        </FormGroup>
      </FormControl>
    </Box>
  );
}

export function ProjectStatesFilterButton({
  allowedStates,
  ...props
}: FilterProps<ProjectStateMachineStatus[]> & {
  allowedStates: ProjectStateMachineStatus[];
}) {
  const { t } = useTranslate("ProjectState");
  const { projectStateOptions } = useProjectStates();
  const options = projectStateOptions.filter(o =>
    allowedStates.includes(o.value)
  );
  if (options.length <= 1) return null;

  return (
    <ButtonInputWithPopover
      label={t("Status")}
      counter={
        props.value &&
        props.value.length > 0 &&
        props.value.length < options.length
          ? props.value.length
          : undefined
      }
      content={
        <ProjectStatesFilterForm allowedStates={allowedStates} {...props} />
      }
      popoverWidth="md"
    />
  );
}

export function ProjectStatesSelect({
  allowedStates,
  value,
  setValue,
  ...props
}: FilterProps<ProjectStateMachineStatus[]> & {
  allowedStates: ProjectStateMachineStatus[];
}) {
  const { t } = useTranslate("ProjectState");
  const { projectStateOptions } = useProjectStates();

  const options = projectStateOptions.filter(o =>
    allowedStates.includes(o.value)
  );
  if (options.length <= 1) return null;

  return (
    <SelectMultiple
      label={t("Project State")}
      options={options}
      value={Array.isArray(value) ? value : [value]}
      onChange={setValue}
      {...props}
    />
  );
}

export function ProjectStatesFilterChips(
  props: Omit<
    FilterChipGroupProps<ProjectStateMachineStatus>,
    "label" | "getValueLabel"
  >
) {
  const { t } = useTranslate("ProjectState");
  const { projectStateLabels } = useProjectStates();

  return (
    <FilterChipGroup
      {...props}
      getValueLabel={value => projectStateLabels[value]}
      label={t("Status")}
    />
  );
}
